import { CheckAccessToken } from '@mdd/api';

// The value of an access_token parameter anywhere in a string, such as a URL.
const ACCESS_TOKEN_PARAM = /(access_token=)[^&#\s"']+/gi;

// Login returns the bearer token in the URL fragment (#access_token=<JWT>).
// This stores the token and removes it from the URL, so Sentry and PostHog
// never record it. Call it before initSentry() and initPostHog().
export function consumeAccessTokenFromUrl(): void {
    const hash = window.location.hash;
    if (!hash) {
        return;
    }

    try {
        // Pass a copy, not window.location. ParseAccessToken writes the cleaned
        // hash back to it, and writing window.location.hash adds a history entry.
        // ParseAccessToken cleans the real URL itself, with history.replaceState.
        new CheckAccessToken().execute(<Location>{ hash, search: '' });
    } catch (error) {
        // Decoding or storing the token failed. ParseAccessToken has already
        // removed it from the URL, so the user lands on the login page. Throw
        // again once Sentry is running, so the failure is still reported.
        setTimeout(() => {
            throw error;
        });
    }
}

// Returns a copy of value with every access token in its strings redacted.
// Copies plain objects, arrays, and objects with toJSON. Keeps other objects as they are.
// copies maps each object already visited to its redacted copy, so an object
// that appears twice is redacted both times, and cycles still end.
export function redactAccessTokens<T>(value: T, copies = new WeakMap<object, unknown>()): T {
    if (typeof value === 'string') {
        return <T>(<unknown>value.replace(ACCESS_TOKEN_PARAM, '$1[Filtered]'));
    }

    if (!value || typeof value !== 'object' || value instanceof Date) {
        return value;
    }

    if (copies.has(value)) {
        return <T>copies.get(value);
    }

    // The browser's navigation timing entry keeps the login URL, token included,
    // after the address bar is cleaned. Both SDKs send these entries, which
    // expose their fields only through toJSON, so redact what toJSON returns.
    const toJSON = (<{ toJSON?: unknown }>value).toJSON;
    if (typeof toJSON === 'function') {
        const redacted = redactAccessTokens(<T>toJSON.call(value), copies);
        copies.set(value, redacted);
        return redacted;
    }

    if (Array.isArray(value)) {
        const copy: unknown[] = [];
        copies.set(value, copy);
        value.forEach((item) => copy.push(redactAccessTokens(item, copies)));
        return <T>(<unknown>copy);
    }

    if (Object.getPrototypeOf(value) !== Object.prototype) {
        return value;
    }

    const copy: Record<string, unknown> = {};
    copies.set(value, copy);
    Object.keys(value).forEach((key) => {
        copy[key] = redactAccessTokens((<Record<string, unknown>>value)[key], copies);
    });

    return <T>copy;
}
